import { useState, useEffect } from "react";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Separator } from "@/components/ui/separator";
import { Loader2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/use-auth";
import { apiRequest } from "@/lib/queryClient";

// Form schema - simpler version without refine to troubleshoot
const formSchema = z.object({
  emailAddress: z.string().email("Please enter a valid email address"),
  password: z.string().min(1, "Password is required"),
  imapServer: z.string().min(1, "IMAP server is required"),
  emailProvider: z.string().min(1, "Email provider is required"),
}).superRefine((data, _ctx) => {
  // Explicitly set IMAP server based on provider during validation
  if (data.emailProvider === "Gmail" && data.imapServer === "") {
    // Auto-set Gmail IMAP server
    data.imapServer = "imap.gmail.com";
  } else if (data.emailProvider === "Outlook" && data.imapServer === "") {
    // Auto-set Outlook IMAP server
    data.imapServer = "outlook.office365.com";
  } else if (data.emailProvider === "Yahoo" && data.imapServer === "") {
    // Auto-set Yahoo IMAP server
    data.imapServer = "imap.mail.yahoo.com";
  }
});

type FormValues = z.infer<typeof formSchema>;

// Type for credentials
type EmailCredentials = {
  emailAddress: string;
  imapServer: string;
  emailProvider: string;
  lastFetchDate: string | null;
  id?: number; // Add optional id field to detect OAuth vs manual credentials
  oauthProvider?: string; // Add OAuth provider info from backend
};

export default function EmailConnectionForm() {
  const { toast } = useToast();
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(true); // Start with loading true
  const [isEditMode, setIsEditMode] = useState(false);
  const [credentials, setCredentials] = useState<EmailCredentials | null>(null);
  const [showManualForm, setShowManualForm] = useState(false);
  const [userOAuthProvider, setUserOAuthProvider] = useState<string | null>(null);

  // Initialize form
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      emailAddress: "",
      password: "",
      imapServer: "imap.gmail.com", // Ensure this is actually set
      emailProvider: "Gmail",
    },
    mode: "onChange", // This helps validate as soon as values change
  });

  // Check if current credentials are OAuth-based
  const isOAuthCredentials = credentials && credentials.id === 0 && credentials.imapServer === "oauth";
  const hasManualCredentials = credentials && credentials.id !== 0;

  // Fetch existing credentials
  const fetchCredentials = async () => {
    try {
      setIsLoading(true);
      
      // Debug: Log user OAuth provider
      console.log("=== EmailConnectionForm Debug ===");
      console.log("User object:", user);
      console.log("User OAuth provider:", user?.oauth_provider);
      
      // If user.oauth_provider is missing or 'email', try to fetch fresh info from backend once
      if ((!user?.oauth_provider || user.oauth_provider === 'email') && !userOAuthProvider) {
        const sessionId = localStorage.getItem("sessionId");
        if (sessionId) {
          try {
            const resp = await apiRequest("GET", "/api/auth/user-info", null, {
              headers: {
                Authorization: `Bearer ${sessionId}`
              }
            });
            if (resp.ok) {
              const data = await resp.json();
              if (data.oauth_provider) {
                setUserOAuthProvider(data.oauth_provider);
                console.log("Fetched OAuth provider from backend:", data.oauth_provider);
              }
            }
          } catch (infoErr) {
            console.warn("Could not fetch user-info endpoint:", infoErr);
          }
        }
      }
      
      // Get the session ID from localStorage
      const sessionId = localStorage.getItem("sessionId");
      if (!sessionId) {
        console.error("No session ID found in localStorage");
        setIsLoading(false);
        return;
      }
      
      try {
        const response = await apiRequest("GET", "/api/email-credentials/", null, {
          headers: {
            Authorization: `Bearer ${sessionId}`
          }
        });
        
        if (response.ok) {
          const data = await response.json();
          
          if (data) {
            // Add OAuth provider info to credentials from user object
            if (data.emailProvider === 'oauth') {
              data.oauthProvider = user?.oauth_provider || userOAuthProvider || null;
            }
            setCredentials(data);
            
            // Update form with existing values except password
            form.setValue("emailAddress", data.emailAddress || "");
            
            // First set the provider, which would normally auto-set the IMAP server
            form.setValue("emailProvider", data.emailProvider || "Gmail");
            
            // Then explicitly set the IMAP server from the data to override any auto-setting
            form.setValue("imapServer", data.imapServer || "imap.gmail.com");
            
            // Trigger validation for both fields
            form.trigger(["emailProvider", "imapServer"]);
          }
        } else if (response.status === 404) {
          // 404 is expected for new users who haven't set up email
          setCredentials(null);
        } else {
          throw new Error(`Unexpected response: ${response.status}`);
        }
      } catch (err: unknown) {
        const error = err as { message?: string };
        if (error?.message && error.message.includes("404")) {
          // 404 is expected for new users who haven't set up email
          setCredentials(null);
        } else {
          console.error("Failed to fetch email credentials:", error);
        }
      }
    } catch (error) {
      console.error("Error in fetchCredentials:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch credentials on mount
  useEffect(() => {
    if (user) {
      fetchCredentials();
    } else {
      // If no user, set loading to false
      setIsLoading(false);
    }
  }, [user]);

  const onSubmit = async (data: FormValues) => {
    try {
      setIsLoading(true);
      
      if (!localStorage.getItem("sessionId")) {
        console.error("No session ID found");
        toast({
          title: "Error",
          description: "Authentication error. Please log in again.",
          variant: "destructive",
        });
        return;
      }
      

      
      try {
        // Get the session ID from localStorage
        const sessionId = localStorage.getItem("sessionId");
        if (!sessionId) {
          throw new Error("No session ID found");
        }
        
        // Always try to use POST first for creating new credentials
        try {
          await apiRequest("POST", "/api/email-credentials/", data, {
            headers: {
              Authorization: `Bearer ${sessionId}`,
              "Content-Type": "application/json"
            }
          });
          
          toast({
            title: "Success",
            description: "Email connection set up successfully",
          });
        } catch (postError: any) {
          // If POST fails with 400 "already exists", then try PUT
          if (postError?.message && postError.message.includes("400")) {
            // Now try to update with PUT
            await apiRequest("PUT", "/api/email-credentials/", data, {
              headers: {
                Authorization: `Bearer ${sessionId}`,
                "Content-Type": "application/json"
              }
            });
            
            toast({
              title: "Success",
              description: "Email connection updated successfully",
            });
          } else {
            // Some other error occurred with the POST request
            throw postError;
          }
        }
        
        // Refresh credentials
        await fetchCredentials();
        setIsEditMode(false);
      } catch (err: unknown) {
        const error = err as { message?: string };
        console.error("Error submitting credentials:", error);
        
        // If we get an auth error, try to guide the user
        if (error?.message && error.message.includes("401")) {
          toast({
            title: "Authentication Error",
            description: "Your session has expired. Please log out and log in again.",
            variant: "destructive",
          });
        } else {
          toast({
            title: "Error",
            description: "Failed to save email connection settings: " + (error?.message || "Unknown error"),
            variant: "destructive",
          });
        }
      }
    } catch (error) {
      console.error("Outer error in submit:", error);
      toast({
        title: "Error",
        description: "An unexpected error occurred",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Helper for common email providers
  const getImapServer = (provider: string) => {
    switch (provider) {
      case "Gmail":
        return "imap.gmail.com";
      case "Outlook":
        return "outlook.office365.com";
      case "Yahoo":
        return "imap.mail.yahoo.com";
      default:
        return "";
    }
  };

  // Update IMAP server when provider changes
  const handleProviderChange = (value: string) => {
    form.setValue("emailProvider", value);
    
    // Only update IMAP server if it's a known provider or empty
    const currentImapServer = form.getValues("imapServer");
    const newImapServer = getImapServer(value);
    
    // If provider changed to a known provider or the imap server is empty/default, update it
    if (newImapServer && (currentImapServer === "" || 
        currentImapServer === "imap.gmail.com" || 
        currentImapServer === "outlook.office365.com" || 
        currentImapServer === "imap.mail.yahoo.com")) {
      form.setValue("imapServer", newImapServer);
    }
    
    // Always validate both fields
    form.trigger(["emailProvider", "imapServer"]);
  };

  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle>Email Connection</CardTitle>
        <CardDescription>
          Configure your email to fetch recruiting emails. We'll use these settings to scan
          your inbox for soccer recruiting emails and extract player information.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4 sm:space-y-6">
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : !showManualForm && !isEditMode ? (
          <div className="space-y-6">
            {/* OAuth Option */}
            <div className="border rounded-lg p-3 sm:p-4 bg-gradient-to-r from-blue-50 to-green-50">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0">
                <div className="flex items-start sm:items-center space-x-3">
                  <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                    {/* ... existing icon code ... */}
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="font-semibold text-sm">Use Your OAuth Account</h3>
                    <p className="text-sm text-muted-foreground break-all">{credentials?.emailAddress || user?.email}</p>
                    {isOAuthCredentials && (
                      <div className="flex items-center mt-1">
                        <div className="w-2 h-2 bg-green-500 rounded-full mr-2"></div>
                        <span className="text-xs text-green-600 font-medium">Active</span>
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex-shrink-0 self-start sm:self-center">
                  {isOAuthCredentials ? (
                    <span className="text-xs text-green-600 bg-green-100 px-2 py-1 rounded-full whitespace-nowrap">
                      ✓ Connected
                    </span>
                  ) : (
                    <Button variant="outline" size="sm" className="text-xs">
                      Use This Email
                    </Button>
                  )}
                </div>
              </div>
              {isOAuthCredentials && (
                <div className="mt-3 text-xs text-muted-foreground">
                  Automatically using your OAuth email for recruiting communications.
                  {credentials?.lastFetchDate && (
                    <span className="block mt-1">
                      Last sync: {new Date(credentials.lastFetchDate).toLocaleString()}
                    </span>
                  )}
                </div>
              )}
            </div>

            {/* Manual Credentials Option */}
            <div className="border rounded-lg p-3 sm:p-4">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0">
                <div className="flex items-start sm:items-center space-x-3">
                  <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
                    <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="font-semibold text-sm">Use Different Email Credentials</h3>
                    <p className="text-sm text-muted-foreground break-words">
                      {hasManualCredentials ? credentials?.emailAddress : "Set up organizational or personal email"}
                    </p>
                    {hasManualCredentials && (
                      <div className="flex items-center mt-1">
                        <div className="w-2 h-2 bg-green-500 rounded-full mr-2"></div>
                        <span className="text-xs text-green-600 font-medium">Active</span>
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex-shrink-0 self-start sm:self-center">
                  {hasManualCredentials ? (
                    <div className="flex flex-col sm:flex-row gap-2 sm:justify-center sm:items-center">
                      <span className="text-xs text-green-600 bg-green-100 px-2 py-1 rounded-full whitespace-nowrap">
                        ✓ Connected
                      </span>
                      <Button 
                        variant="outline" 
                        size="sm"
                        className="text-xs"
                        onClick={() => {
                          form.setValue("emailAddress", credentials?.emailAddress || "");
                          form.setValue("emailProvider", credentials?.emailProvider || "Gmail");
                          form.setValue("imapServer", credentials?.imapServer || "imap.gmail.com");
                          form.setValue("password", "");
                          form.trigger(["emailProvider", "imapServer"]);
                          setIsEditMode(true);
                          setShowManualForm(true);
                        }}
                      >
                        Edit
                      </Button>
                    </div>
                  ) : (
                    <Button 
                      variant="outline" 
                      size="sm"
                      className="text-xs"
                      onClick={() => setShowManualForm(true)}
                    >
                      Set Up Email
                    </Button>
                  )}
                </div>
              </div>
            </div>

            {/* Help Text */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="font-medium text-blue-900 mb-2">Which option should I choose?</h4>
              <ul className="text-sm text-blue-800 space-y-1">
                <li>• <strong>OAuth Email:</strong> Automatic setup, uses your sign-in email ({user?.email})</li>
                <li>• <strong>Different Email:</strong> For work/organizational emails, or if you want to use a different personal account</li>
              </ul>
            </div>
          </div>
        ) : (
          <Form {...form}>
            <Alert className="mb-6 border-amber-200 bg-amber-50">
              <AlertDescription className="text-amber-800">
                <strong>Password Setup:</strong> You'll need your email provider's app password (NOT your Bloom login password). 
                For Gmail users, this is a 16-character code like "mjhc lorm rrwj ydym" that you generate in your Google Account settings.
              </AlertDescription>
            </Alert>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              <FormField
                control={form.control}
                name="emailProvider"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email Provider</FormLabel>
                    <Select
                      disabled={isLoading}
                      onValueChange={handleProviderChange}
                      value={field.value}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select email provider" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="Gmail">Gmail</SelectItem>
                        <SelectItem value="Outlook">Outlook</SelectItem>
                        <SelectItem value="Yahoo">Yahoo</SelectItem>
                        <SelectItem value="Custom">Other (Custom)</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormDescription>
                      Select your email provider to automatically set up the IMAP server.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="emailAddress"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email Address</FormLabel>
                    <FormControl>
                      <Input
                        disabled={isLoading}
                        placeholder="your.email@example.com"
                        {...field}
                      />
                    </FormControl>
                    <FormDescription>
                      The email address you use for soccer recruiting communications.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email App Password</FormLabel>
                    <FormControl>
                      <Input
                        disabled={isLoading}
                        type="password"
                        placeholder="••••••••••••"
                        {...field}
                      />
                    </FormControl>
                    <FormDescription>
                      <div className="space-y-1">
                        <p className="font-medium text-amber-600">⚠️ Important: This is NOT your Bloom login password!</p>
                        <p>For Gmail, you need to create an <strong>App Password</strong> (like: mjhc lorm rrwj ydym).</p>
                        <p>
                          <a
                            href="https://support.google.com/accounts/answer/185833"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-primary underline"
                          >
                            → Click here to create a Gmail App Password
                          </a>
                        </p>
                        <p className="text-xs text-muted-foreground">
                          If you don't have 2FA enabled, you may be able to use your regular Gmail password.
                        </p>
                      </div>
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="imapServer"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>IMAP Server</FormLabel>
                    <FormControl>
                      <div className="relative">
                        <Input
                          disabled={isLoading || form.getValues("emailProvider") !== "Custom"}
                          placeholder="imap.example.com"
                          className={form.getValues("emailProvider") !== "Custom" ? "bg-neutral-50" : ""}
                          {...field}
                        />
                        {form.getValues("emailProvider") !== "Custom" && (
                          <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                            <span className="text-xs text-neutral-500 bg-neutral-100 px-1.5 py-0.5 rounded">
                              Auto-set
                            </span>
                          </div>
                        )}
                      </div>
                    </FormControl>
                    <FormDescription>
                      {form.getValues("emailProvider") !== "Custom" 
                        ? "This field is automatically set based on your email provider."
                        : "Enter the IMAP server address for your custom email provider."}
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <Alert>
                <AlertDescription>
                  Your email credentials are securely stored and only used to fetch recruiting emails.
                  We recommend using an app-specific password for added security.
                </AlertDescription>
              </Alert>


              
              <div className="flex gap-2">
                {isLoading ? (
                  <Button disabled>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Please wait
                  </Button>
                ) : (
                  <>
                    <Button 
                      type="submit"
                      onClick={() => {
                        // Force the IMAP server to be set correctly for this provider
                        const provider = form.getValues("emailProvider");
                        const serverAddress = getImapServer(provider);
                        if (provider !== "Custom" && serverAddress) {
                          form.setValue("imapServer", serverAddress, { shouldValidate: true });
                        }
                        

                      }}
                    >
                      {credentials ? "Update Connection" : "Save Connection"}
                    </Button>
                    {isEditMode && (
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => {
                          setIsEditMode(false);
                          setShowManualForm(false);
                          fetchCredentials();
                        }}
                      >
                        {hasManualCredentials ? "Cancel" : "Back"}
                      </Button>
                    )}
                    {showManualForm && !isEditMode && (
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => {
                          setShowManualForm(false);
                          form.reset();
                        }}
                      >
                        Back to Options
                      </Button>
                    )}
                  </>
                )}
              </div>
            </form>
          </Form>
        )}
      </CardContent>
      <CardFooter className="flex flex-col items-start">
        <Separator className="mb-4" />
        <div className="text-sm text-muted-foreground">
          <p>
            After setting up your email connection, you can use the "Refresh Players" button on the
            Player Database page to scan your inbox for recruiting emails and extract player information.
          </p>
        </div>
      </CardFooter>
    </Card>
  );
}